import courseMapper from 'core/mappers/course';
import contentLoader from 'core/http/contentLoader';
import { guidWithHyphens } from 'utils/object';
import * as questionActions from 'store/questions/actions';
import { contentsLoaded } from 'store/contents/actions';
import * as sectionActions from 'store/sections/actions';
import {
  initializeXapi,
  subscribeXapi,
  unsubscribeXapi,
  initializeScorm
} from 'store/modules/actions';
import { isXapiInitialized, shouldInitializeXapi } from 'store/modules/selectors';
import { getAffectProgressSections } from 'store/sections/selectors';
import { getValueFromUrl } from 'utils/window';
import {
  getCourse,
  getAttemptId,
  hasBeenContinued,
  isCourseAccessLimited
} from 'store/course/selectors';
import eventEmitter, { events } from 'core/events/eventEmitter';
import { getUser } from 'store/user/selectors';
import { getQuestions } from 'store/questions/selectors';
import { isPreviewMode, isScormMode } from 'store/settings/selectors';
import progressStorage from 'core/progressStorage';
import { INDEX_PATH, NOACCESS_PATH } from 'constants/navigation';
import { getSections } from '../sections/selectors';
import { MASTERY_SCORE } from '../../constants/common';

export const COURSE_DATA_LOADING_STARTED = 'COURSE_DATA_LOADING_STARTED';
export const COURSE_DATA_LOADED = 'COURSE_DATA_LOADED';
export const COURSE_DATA_LOADING_FAILED = 'COURSE_DATA_LOADING_FAILED';
export const COURSE_SCORE_UPDATED = 'COURSE_SCORE_UPDATED';
export const COURSE_LAUNCHED = 'COURSE_LAUNCHED';
export const COURSE_STARTED = 'COURSE_STARTED';
export const COURSE_PROGRESS_RESTORED = 'COURSE_PROGRESS_RESTORED';
export const COURSE_CLEANUP = 'COURSE_CLEANUP';
export const COURSE_FINISHED = 'COURSE_FINISHED';
export const COURSE_FINALIZED = 'COURSE_FINALIZED';
export const CLEAR_PREVIEW_QUESTION_ID = 'CLEAR_PREVIEW_QUESTION_ID';
export const CERTIFICATE_DOWNLOADED = 'CERTIFICATE_DOWNLOADED';

export const updateScore = () => async (dispatch, getState) => {
  const sections = getAffectProgressSections(getState());
  const totalScore = sections.reduce((sum, section) => sum + section.score, 0);
  const score = sections.length ? Math.floor(totalScore / sections.length) : 100;
  dispatch({
    type: COURSE_SCORE_UPDATED,
    payload: {
      score,
      isPassed: score === MASTERY_SCORE
    }
  });
};

export const updateProgress = () => async (dispatch, getState) => {
  dispatch(updateScore());
  eventEmitter.emit(events.COURSE_PROGRESSED, getCourse(getState()));
};

export const load = () => async (dispatch, getState) => {
  dispatch({ type: COURSE_DATA_LOADING_STARTED });
  try {
    const data = await contentLoader.loadCourseData();
    let { course, sections, questions, contents } = courseMapper.map(data);
    if (getValueFromUrl('questionId')) {
      course.previewQuestionId = getValueFromUrl('questionId');
    }
    dispatch(sectionActions.sectionsLoaded(sections));
    dispatch(questionActions.questionsLoaded(questions));
    dispatch(contentsLoaded(contents));
    await dispatch({ type: COURSE_DATA_LOADED, payload: course });
    eventEmitter.emit(events.APP_INITIALIZED, { state: getState() });
  } catch (e) {
    dispatch({ type: COURSE_DATA_LOADING_FAILED, reason: e });
    throw e;
  }
};

export const restoreProgress = () => async dispatch => {
  const isProgressRestored = await progressStorage.restoreProgress();
  if (!isProgressRestored) {
    return;
  }

  progressStorage.answers.forEach(({ id, response }) => {
    dispatch(questionActions.restoreProgress(id, response));
  });

  dispatch({
    type: COURSE_PROGRESS_RESTORED,
    payload: { attemptId: progressStorage.attemptId }
  });
};

export const onCourseLaunched = attemptId => dispatch => {
  dispatch({
    type: COURSE_LAUNCHED,
    payload: { attemptId }
  });
  eventEmitter.emit(events.COURSE_LAUNCHED, attemptId);
};

export const onCourseStarted = () => dispatch => {
  dispatch({
    type: COURSE_STARTED
  });

  eventEmitter.emit(events.COURSE_STARTED);
};

export const launch = () => async (dispatch, getState) => {
  if (isPreviewMode(getState())) {
    dispatch(onCourseLaunched());
    dispatch(onCourseStarted());
    return INDEX_PATH;
  }

  if (isScormMode(getState())) {
    await dispatch(initializeScorm());
  } else {
    if (isCourseAccessLimited(getState())) {
      return NOACCESS_PATH;
    }
    await eventEmitter.emit(events.APP_INITIALIZED, { state: getState() });
    await eventEmitter.emit(events.USER_AUTHENTICATED, getUser(getState()));
  }

  await dispatch(restoreProgress());

  let attemptId = getAttemptId(getState());
  if (!attemptId) {
    attemptId = guidWithHyphens();
  }

  // TODO: events
  dispatch(onCourseLaunched(attemptId));

  const state = getState();
  if (shouldInitializeXapi(state)) {
    await dispatch(initializeXapi());
    await dispatch(subscribeXapi());
  }

  if (hasBeenContinued(getState())) {
    return progressStorage.url || INDEX_PATH;
  }

  dispatch(onCourseStarted());

  return INDEX_PATH;
};

export const cleanup = () => async (dispatch, getState) => {
  dispatch({
    type: COURSE_CLEANUP
  });

  const sections = getSections(getState());
  sections.forEach(section => {
    dispatch(sectionActions.cleanup(section.id));
  });
  const questions = getQuestions(getState());
  questions.forEach(question => {
    dispatch(questionActions.cleanup(question.id));
  });

  if (isXapiInitialized(getState())) {
    await dispatch(unsubscribeXapi());
  }
};

export const finished = () => async (dispatch, getState) => {
  dispatch({
    type: COURSE_FINISHED
  });
  eventEmitter.emit(events.COURSE_FINISHED, getCourse(getState()));
};

export const finalized = () => async dispatch => {
  dispatch({
    type: COURSE_FINALIZED
  });
  eventEmitter.emit(events.COURSE_FINALIZED);
};

export const startNewAttempt = () => async dispatch => {
  await dispatch(cleanup());
  await progressStorage.removeProgress();

  await dispatch(launch());
};

export const clearPreviewQuestionId = () => dispatch => {
  dispatch({ type: CLEAR_PREVIEW_QUESTION_ID });
};

export const certificateDownloaded = isCertificateDownloaded => dispatch => {
  dispatch({
    type: CERTIFICATE_DOWNLOADED,
    payload: { isCertificateDownloaded }
  });
};

export const evaluate = ({ score, response }) => () => {
  eventEmitter.emit(events.COURSE_EVALUATED, {
    score,
    response
  });
};
